// Public Support page — accessible to anyone (no auth).
// Tabs: FAQs / Submit a Request.
// Reachable from the public marketing site at /support and /contact.

import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import {
  Accordion, AccordionItem, AccordionTrigger, AccordionContent,
} from '@/components/ui/accordion';
import { ArrowLeft, MessageCircleQuestion, FileQuestion, Loader2, Sparkles } from 'lucide-react';
import SupportRequestForm from '@/components/support/SupportRequestForm';
import logger from '@/lib/logger';

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PublicSupportPage() {
  const [tab, setTab] = useState('faqs');
  const [faqs, setFaqs] = useState([]);
  const [loadingFaqs, setLoadingFaqs] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/public/faqs`);
        if (!res.ok) throw new Error('faqs failed');
        const data = await res.json();
        setFaqs(data.items || []);
      } catch (e) {
        logger.warn('PublicSupport: FAQs failed', e);
      } finally {
        setLoadingFaqs(false);
      }
    })();
  }, []);

  // Group FAQs by category for a nicer layout
  const groupedFaqs = faqs.reduce((acc, f) => {
    (acc[f.category] = acc[f.category] || []).push(f);
    return acc;
  }, {});

  return (
    <div className="min-h-screen bg-[#FAF8F5] text-[#2C2420]">
      {/* Top nav */}
      <header className="border-b border-[#E6E0D9] bg-white sticky top-0 z-30">
        <div className="max-w-5xl mx-auto px-6 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-sm font-medium text-[#2C2420]" data-testid="public-support-home-link">
            <ArrowLeft className="w-4 h-4" />
            <span className="hidden sm:inline">QIndex One</span>
            <span className="sm:hidden">Home</span>
          </Link>
          <div className="flex items-center gap-2">
            <Link to="/login">
              <Button size="sm" variant="outline" data-testid="public-support-login-btn">Log in</Button>
            </Link>
            <Link to="/request-pilot">
              <Button size="sm" className="bg-[#4A3B32] hover:bg-[#3A2E26]" data-testid="public-support-pilot-btn">
                <Sparkles className="w-3.5 h-3.5 mr-1.5" /> Request a pilot
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-5xl mx-auto px-6 pt-12 pb-6 text-center">
        <p className="text-xs uppercase tracking-[0.2em] text-[#A86A3A] mb-3">Support</p>
        <h1 className="text-4xl sm:text-5xl font-light mb-4 leading-tight">How can we help?</h1>
        <p className="text-base text-[#5D524D] max-w-xl mx-auto">
          Find quick answers in our FAQs, or send us a message — we read every request and respond by email.
        </p>
      </section>

      {/* Tabs */}
      <section className="max-w-3xl mx-auto px-6 pb-20">
        <Tabs value={tab} onValueChange={setTab} className="w-full">
          <TabsList className="grid grid-cols-2 w-full bg-white border border-[#E6E0D9] p-1 h-auto" data-testid="public-support-tabs">
            <TabsTrigger value="faqs" className="gap-2 data-[state=active]:bg-[#4A3B32] data-[state=active]:text-white" data-testid="public-support-tab-faqs">
              <FileQuestion className="w-4 h-4" />
              FAQs
            </TabsTrigger>
            <TabsTrigger value="submit" className="gap-2 data-[state=active]:bg-[#4A3B32] data-[state=active]:text-white" data-testid="public-support-tab-submit">
              <MessageCircleQuestion className="w-4 h-4" />
              Submit a Request
            </TabsTrigger>
          </TabsList>

          {/* FAQs */}
          <TabsContent value="faqs" className="mt-6">
            {loadingFaqs ? (
              <div className="flex items-center justify-center py-16 text-[#5D524D]" data-testid="public-faqs-loading">
                <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading FAQs…
              </div>
            ) : faqs.length === 0 ? (
              <div className="text-center text-[#5D524D] py-16 border border-dashed border-[#E6E0D9] rounded-lg bg-white">
                FAQs are being prepared.
              </div>
            ) : (
              <div className="space-y-6" data-testid="public-faqs-list">
                {Object.entries(groupedFaqs).map(([category, items]) => (
                  <div key={category}>
                    <h2 className="text-xs uppercase tracking-wider text-[#8C8078] mb-2 px-1 font-semibold">{category}</h2>
                    <Accordion type="single" collapsible className="bg-white border border-[#E6E0D9] rounded-lg overflow-hidden">
                      {items.map(faq => (
                        <AccordionItem key={faq.id} value={faq.id} className="border-b last:border-b-0 border-[#E6E0D9]">
                          <AccordionTrigger className="px-5 hover:bg-[#F9F7F5] text-left text-sm font-medium" data-testid={`faq-${faq.id}`}>
                            {faq.question}
                          </AccordionTrigger>
                          <AccordionContent className="px-5 pb-4 text-sm text-[#5D524D] leading-relaxed">
                            {faq.answer}
                          </AccordionContent>
                        </AccordionItem>
                      ))}
                    </Accordion>
                  </div>
                ))}
                <div className="mt-8 text-center text-sm text-[#5D524D]">
                  Didn't find what you were after?{' '}
                  <button
                    onClick={() => setTab('submit')}
                    className="text-[#A86A3A] underline underline-offset-2 font-medium"
                    data-testid="public-support-jump-to-form"
                  >
                    Submit a request →
                  </button>
                </div>
              </div>
            )}
          </TabsContent>

          {/* Submit a Request */}
          <TabsContent value="submit" className="mt-6">
            <SupportRequestForm mode="public" />
            <p className="text-xs text-[#8C8078] mt-4 text-center">
              Looking to trial QIndex One instead?{' '}
              <Link to="/request-pilot" className="text-[#A86A3A] underline underline-offset-2">
                Request a private pilot →
              </Link>
            </p>
          </TabsContent>
        </Tabs>
      </section>

      {/* Footer */}
      <footer className="border-t border-[#E6E0D9] bg-white">
        <div className="max-w-5xl mx-auto px-6 py-6 text-xs text-[#8C8078] flex items-center justify-between flex-wrap gap-2">
          <span>© {new Date().getFullYear()} QIndex One Pty Ltd</span>
          <Link to="/" className="hover:text-[#2C2420]">Back to home</Link>
        </div>
      </footer>
    </div>
  );
}
